import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Button, Dropdown, Form, Message, TextArea } from 'semantic-ui-react';

import classNames from 'classnames';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import Modal from '../Modal';

import '../../css/GradebookPlacementAddToGroupModal.less';

const STEP = { SELECT_GROUP: 1, CREATE_GROUP: 2, CONFIRM: 3 };

const GradebookPlacementAddToGroupModal = observer((props) => {
  const {
    classroomId,
    onClose,
    open,
    t,
  } = props;

  const {
    gradebookManager,
    groupsManager,
  } = useContext(MobXProviderContext);

  // useRef so SatCoreComponent is resolved only once per mount
  const ModalBanner = useRef(SatCoreComponent('ModalBanner')).current;
  const SatCoreLoader = useRef(SatCoreComponent('SatCoreLoader')).current;

  /* ── Step 1: group selection ── */
  const [step, setStep] = useState(STEP.SELECT_GROUP);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupMode, setGroupMode] = useState('add'); // 'add' | 'create'

  /* ── Step 2: create group form ── */
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [groupFormErrors, setGroupFormErrors] = useState({});

  /* ── Step 3: confirm / submit ── */
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { placementGroups, placementGroupsLoaded } = groupsManager;

  const checkedInstances = Array.from(
    gradebookManager.assignmentToRepInstanceBulkCheckedMap.values()
  );

  useEffect(() => {
    if (open && classroomId) {
      setStep(STEP.SELECT_GROUP);
      setSelectedGroupId(null);
      setGroupMode('add');
      setGroupName('');
      setGroupDescription('');
      setGroupFormErrors({});
      setSubmitError(null);
      groupsManager.fetchGroupsForPlacement(classroomId);
    }
  }, [open]);

  /* ── Step 1 handlers ── */
  const handleOpenCreateGroup = () => {
    setGroupMode('create');
    setGroupName('');
    setGroupDescription('');
    setGroupFormErrors({});
    setStep(STEP.CREATE_GROUP);
  };

  const handleGroupSave = () => {
    setGroupMode('add');
    setSubmitError(null);
    setStep(STEP.CONFIRM);
  };

  /* ── Step 2 handlers ── */
  const handleCreateGroupBack = () => setStep(STEP.SELECT_GROUP);

  const handleCreateGroupNext = () => {
    const errors = {};
    if (!groupName.trim()) errors.groupName = true;
    if (groupName.length > 30) errors.groupNameLength = true;
    if (Object.keys(errors).length) {
      setGroupFormErrors(errors);
      return;
    }
    setSubmitError(null);
    setStep(STEP.CONFIRM);
  };

  /* ── Step 3 handlers ── */
  const handleBack = () => setStep(groupMode === 'add' ? STEP.SELECT_GROUP : STEP.CREATE_GROUP);

  const handleOk = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const studentIds = checkedInstances.map((i) => i.userId).filter(Boolean);
      if (groupMode === 'create') {
        const response = await groupsManager.createClassroomGroupWithMembers(
          classroomId, groupName.trim(), groupDescription.trim(), studentIds
        );
        if (response?.status === 'SUCCESS') {
          onClose();
        } else {
          const msg = response?.statusMessage || '';
          if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('name')) {
            setSubmitError(t('placementAddToGroupNameError',
              'That group name is already in use. Please choose another.'));
          } else {
            setSubmitError(t('placementAddToGroupCreateError',
              'Failed to create group. Please try again.'));
          }
        }
      } else {
        const success = await groupsManager.addMembersToClassroomGroup(selectedGroupId, studentIds);
        if (success) {
          onClose();
        } else {
          setSubmitError(t('placementAddToGroupError',
            'Failed to add students to group. Please try again.'));
        }
      }
    } catch (e) {
      setSubmitError(t('placementAddToGroupError',
        'Failed to add students to group. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── renderers ── */
  const renderSelectGroupStep = () => {
    const hasGroups = placementGroups.length > 0;
    const groupOptions = placementGroups.map((group) => ({
      key: group.id,
      text: group.name,
      value: group.id,
    }));

    return (
      <>
        <Modal.Content>
          {!placementGroupsLoaded ? (
            <SatCoreLoader height='200px' t={t} />
          ) : (
            <div className='placement-select-group-content'>
              <p className='placement-group-note'>
                <strong>{t('placementAddToGroupNote', 'NOTE: Groups can only be used within a class.')}</strong>
              </p>
              {hasGroups ? (
                <>
                  <p className='placement-group-select-label'>
                    {t('placementAddToGroupSelectLabel', 'Select from existing groups in this classroom:')}
                  </p>
                  <Dropdown
                    className='placement-group-dropdown'
                    onChange={(_e, { value }) => setSelectedGroupId(value)}
                    options={groupOptions}
                    placeholder={t('placementAddToGroupDropdownPlaceholder', 'Select a group')}
                    selection
                    value={selectedGroupId} />
                  <div className='placement-group-create-btn-wrapper'>
                    <Button className='btn-create-group' onClick={handleOpenCreateGroup} primary type='button'>
                      {t('placementAddToGroupCreateGroupButton', 'Create a Group')}
                    </Button>
                  </div>
                </>
              ) : (
                <div className='placement-group-zero-state'>
                  <p>{t('placementAddToGroupZeroState',
                    'No groups exist for this classroom.')}</p>
                  <Button onClick={handleOpenCreateGroup} primary type='button'>
                    {t('placementAddToGroupCreateGroupButton', 'Create a Group')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic className='cancelButton' onClick={onClose} primary>
            {t('placementAddToClassroomBackButton', 'Back')}
          </Button>
          <Button disabled={!selectedGroupId} onClick={handleGroupSave} primary>
            {t('placementAddToGroupSaveButton', 'Save')}
          </Button>
        </Modal.Actions>
      </>
    );
  };

  const renderCreateGroupStep = () => (
    <>
      <Modal.Content>
        <Form className='placement-create-group-form'>
          <Form.Field required>
            <label>{t('placementCreateGroupNameLabel', 'Group Name')}</label>
            <Form.Input
              className='placement-group-name-input'
              onChange={(_e, { value }) => {
                const exceeded = value.length > 30;
                setGroupName(value.slice(0, 30));
                setGroupFormErrors((prev) => ({
                  ...prev,
                  groupName: false,
                  groupNameLength: exceeded,
                }));
              }}
              placeholder={t('placementCreateGroupNamePlaceholder', 'Group Name')}
              value={groupName} />
            <Message
              content={t('placementCreateGroupNameRequired', 'Group name is required.')}
              error
              visible={!!groupFormErrors.groupName} />
            <Message
              content={t('placementCreateGroupNameTooLong', 'Group name must be 30 characters or less.')}
              error
              visible={!!groupFormErrors.groupNameLength} />
          </Form.Field>
          <Form.Field>
            <label>{t('placementCreateGroupDescriptionLabel', 'Group Description')}</label>
            <TextArea
              onChange={(_e, { value }) => setGroupDescription(value)}
              placeholder={t('placementCreateGroupDescriptionPlaceholder', 'Group Description')}
              rows={4}
              value={groupDescription} />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic className='cancelButton' onClick={handleCreateGroupBack} primary>
          {t('placementAddToClassroomCancelButton', 'Cancel')}
        </Button>
        <Button onClick={handleCreateGroupNext} primary>
          {t('placementAddToClassroomNextButton', 'Next')}
        </Button>
      </Modal.Actions>
    </>
  );

  const selectedGroup = placementGroups.find((g) => g.id === selectedGroupId);

  const renderConfirmStep = () => (
    <>
      <Modal.Content>
        <div className='placement-confirm-content'>
          <div className='placement-confirm-row'>
            <span className='placement-confirm-label'>
              {t('placementAddToGroupConfirmGroupLabel', 'Group:')}
            </span>
            <span className='placement-confirm-value'>
              {groupMode === 'create' ? groupName : selectedGroup?.name}
            </span>
          </div>
          <div className='placement-confirm-row'>
            <span className='placement-confirm-label'>
              {t('placementAddToClassroomConfirmStudentsLabel', 'Students:')}
            </span>
            <span className='placement-confirm-value'>
              {checkedInstances.map((instance) => (
                <span className='placement-confirm-student' key={instance.userId}>
                  {`${instance.firstName} ${instance.lastName}`}
                </span>
              ))}
            </span>
          </div>
          {submitError && (
            <div className='placement-confirm-error'>{submitError}</div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button basic className='cancelButton' disabled={submitting} onClick={handleBack} primary>
          {t('placementAddToClassroomBackButton', 'Back')}
        </Button>
        <Button disabled={submitting} loading={submitting} onClick={handleOk} primary>
          {t('placementAddToClassroomOkButton', 'Ok')}
        </Button>
      </Modal.Actions>
    </>
  );

  const titles = {
    [STEP.SELECT_GROUP]: t('placementAddToGroupSelectGroupTitle', 'Select Existing Group'),
    [STEP.CREATE_GROUP]: t('placementAddToGroupCreateGroupTitle', 'Create Group'),
    [STEP.CONFIRM]: t('placementAddToGroupConfirmTitle', 'Confirm Selection'),
  };

  return (
    <Modal
      className='GradebookPlacementAddToGroupModal'
      closeOnDimmerClick={false}
      closeOnEscape={!submitting}
      onClose={onClose}
      open={open}
      size='small'>
      <ModalBanner onClose={onClose} title={titles[step]} />
      {step === STEP.SELECT_GROUP && renderSelectGroupStep()}
      {step === STEP.CREATE_GROUP && renderCreateGroupStep()}
      {step === STEP.CONFIRM && renderConfirmStep()}
    </Modal>
  );
});

export default GradebookPlacementAddToGroupModal;

SatCoreRegister('GradebookPlacementAddToGroupModal', GradebookPlacementAddToGroupModal);
